import React from 'react';
import {
  Stack,
  ChatMessage,
  ChatMessageList,
  ChatComposer,
  Markdown,
  ToolCall,
  Text,
  groupToolCallParts,
} from '@chatsdet/ui';
import { CircleNotch, Database } from '@phosphor-icons/react';
import type { UserEventsUIMessage } from './types';
import { userEventsToolRenderers, mapToolState, getToolDisplayName } from './toolRenderers';

type Props = {
  messages: UserEventsUIMessage[];
  composerValue: string;
  onComposerChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  isSending: boolean;
  isEnabled: boolean;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return undefined;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const UserEventsChatPanel = React.memo(function UserEventsChatPanel({
  messages,
  composerValue,
  onComposerChange,
  onSend,
  onStop,
  isSending,
  isEnabled,
}: Props) {
  const placeholder = isEnabled
    ? 'Ask a question about user events…'
    : 'Loading conversation…';

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <ChatMessageList className="flex-1 p-2" isStreaming={isSending}>
          {messages.length === 0 ? (
            <EmptyState isEnabled={isEnabled} />
          ) : (
            messages.map((m) => <MessageRenderer key={m.id} message={m} />)
          )}
        </ChatMessageList>
      </div>

      <div className="border-t border-border flex-shrink-0 p-1">
        <ChatComposer
          value={isEnabled ? composerValue : ''}
          onChange={isEnabled ? onComposerChange : () => {}}
          onSubmit={onSend}
          onStop={onStop}
          isSending={isSending}
          submitLabel="Send"
          stopLabel="Stop"
          placeholder={placeholder}
          isDisabled={!isEnabled}
          className="border-none"
        />
      </div>
    </div>
  );
});

const EmptyState: React.FC<{ isEnabled: boolean }> = ({ isEnabled }) => {
  if (!isEnabled) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-[280px] text-center px-4">
        <Database size={32} className="mx-auto mb-3 text-muted-foreground/50" />
        <Text size="sm" className="text-muted-foreground mb-2">
          Ask any question about the user_events table.
        </Text>
        <Text size="xs" className="text-muted-foreground/70">
          e.g. &quot;How many events per day this week?&quot; or &quot;Top 10 orgs by event count&quot;
        </Text>
      </div>
    </div>
  );
};

const MessageRenderer: React.FC<{ message: UserEventsUIMessage }> = ({ message }) => {
  const originalRole = message.metadata?.originalRole ?? message.role;
  const raw = message.metadata?.raw as { createdAt?: string } | undefined;
  const timestamp = raw?.createdAt ? formatDateTime(raw.createdAt) : undefined;

  const taggedParts: Array<{ key: string; node: React.ReactNode; isTool: boolean }> = [];

  for (let i = 0; i < message.parts.length; i++) {
    const part = message.parts[i]!;

    if (
      part.type === 'text' &&
      'text' in part &&
      typeof part.text === 'string' &&
      part.text.trim()
    ) {
      taggedParts.push({
        key: `text-${i}`,
        isTool: false,
        node: (
          <Markdown key={`text-${i}`} className="prose prose-sm max-w-none text-foreground" compact>
            {part.text}
          </Markdown>
        ),
      });
    }

    if (
      part.type === 'reasoning' &&
      'text' in part &&
      typeof part.text === 'string' &&
      part.text.trim()
    ) {
      taggedParts.push({
        key: `reasoning-${i}`,
        isTool: false,
        node: (
          <div
            key={`reasoning-${i}`}
            className="text-xs text-muted-foreground italic border-l-2 border-muted pl-2"
          >
            {part.text}
          </div>
        ),
      });
    }

    if (
      (part.type === 'dynamic-tool' && 'toolName' in part) ||
      (typeof part.type === 'string' && part.type.startsWith('tool-'))
    ) {
      const toolPart = part as {
        type: string;
        toolName?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
      };

      const toolName =
        toolPart.toolName ??
        (toolPart.type.startsWith('tool-') ? toolPart.type.replace(/^tool-/, '') : '');

      if (toolName) {
        const state = mapToolState(toolPart.state ?? 'output-available');
        const CustomRenderer = userEventsToolRenderers[toolName];

        taggedParts.push({
          key: `tool-${i}`,
          isTool: true,
          node: (
            <ToolCall
              key={`tool-${i}`}
              toolName={toolName}
              displayName={getToolDisplayName(toolName)}
              state={state}
              input={toolPart.input}
              output={toolPart.output}
              error={toolPart.errorText}
              grouped
            >
              {CustomRenderer && (
                <CustomRenderer input={toolPart.input} output={toolPart.output} state={state} />
              )}
            </ToolCall>
          ),
        });
      }
    }
  }

  const renderedParts = groupToolCallParts(taggedParts);

  if (renderedParts.length === 0) {
    if (message.role === 'assistant') {
      renderedParts.push(
        <div key="thinking" className="flex items-center gap-2 text-xs text-muted-foreground">
          <CircleNotch size={12} className="animate-spin" />
          <span>Thinking...</span>
        </div>
      );
    } else {
      return null;
    }
  }

  return (
    <ChatMessage
      key={message.id}
      role={originalRole === 'tool' ? 'assistant' : (originalRole as typeof message.role)}
      timestamp={timestamp}
      muted={originalRole === 'system'}
      className="px-3"
    >
      <Stack gap="sm">{renderedParts}</Stack>
    </ChatMessage>
  );
};
